import { InspectionTemplateColumn } from "@/shared/models/Inspection";
import { DragEvent, useState } from "react";
import { ColumnGroup, moveGroup } from "../utils/columnGroups";

const noDragImage = Object.assign(new Image(), {
	src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
});

type Props = {
	groups: ColumnGroup[];
	disabled: boolean;
	onChange: (columns: InspectionTemplateColumn[]) => void;
};

export const useGroupDragReorder = ({ groups, disabled, onChange }: Props) => {
	const [draggedColumn, setDraggedColumn] =
		useState<InspectionTemplateColumn | null>(null);
	const [hovered, setHovered] = useState<InspectionTemplateColumn | null>(null);

	const draggedGroup =
		groups.find(
			(group) =>
				draggedColumn !== null && group.columns.includes(draggedColumn),
		) ?? null;

	const stop = () => {
		setDraggedColumn(null);
		setHovered(null);
	};

	const handleProps = (group: ColumnGroup) => ({
		draggable: !disabled,
		onDragStart: (event: DragEvent<HTMLElement>) => {
			event.dataTransfer.setDragImage(noDragImage, 0, 0);
			setDraggedColumn(group.columns[0]);
		},
		onDragEnd: stop,
	});

	const dropTargetProps = (group: ColumnGroup) => ({
		onDragOver: (event: DragEvent<HTMLElement>) => event.preventDefault(),
		onDragEnter: () => {
			if (hovered === group.columns[0]) return;

			setHovered(group.columns[0]);

			if (draggedGroup === null || draggedGroup === group) return;

			onChange(
				moveGroup(groups, groups.indexOf(draggedGroup), groups.indexOf(group)),
			);
		},
		onDrop: (event: DragEvent<HTMLElement>) => {
			event.preventDefault();
			stop();
		},
	});

	return { draggedGroup, handleProps, dropTargetProps };
};
